import { useState } from "react";
import { Check, Calendar, Mail, MessageSquare } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Container,
  Eyebrow,
  SectionLabel,
  SurfaceCard,
} from "@/components/shared/Primitives";
import { RadarRings } from "@/components/shared/Decorations";
import { Reveal } from "@/components/shared/Reveal";
import { BUDGET_OPTIONS, CONTACT_FAQS, tint } from "@/data/site";

const EMPTY = { name: "", email: "", company: "", budget: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [sent, setSent] = useState(false);

  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Your name is required";
    if (!form.email.trim()) e.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) e.email = "Enter a valid email";
    if (!form.message.trim()) e.message = "Tell us a little about your goals";
    else if (form.message.trim().length < 10) e.message = "A few more words, please";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = (ev) => {
    ev.preventDefault();
    if (!validate()) {
      toast.error("Please fix the highlighted fields");
      return;
    }
    setSent(true);
    toast.success("Message sent — we'll reply within 1 business day.");
    setForm(EMPTY);
    setTimeout(() => setSent(false), 4000);
  };

  const fieldStyle = (k) => ({
    background: "var(--input-bg)",
    border: `1px solid ${errors[k] ? "rgba(239,68,68,0.6)" : "var(--stroke-1)"}`,
    color: "var(--text-0)",
  });

  return (
    <div>
      {/* HERO + FORM */}
      <section className="relative overflow-hidden">
        <div className="pointer-events-none absolute -left-44 -top-36 h-[600px] w-[600px] opacity-50" style={{ color: "var(--page-accent)" }}>
          <RadarRings className="h-full w-full" />
        </div>
        <Container className="relative z-10 grid gap-12 pb-20 pt-16 lg:grid-cols-[0.9fr_1.1fr] lg:pt-24">
          <div>
            <Reveal><Eyebrow>Contact</Eyebrow></Reveal>
            <Reveal delay={0.05}>
              <h1 className="font-display mt-7" style={{ fontSize: "clamp(58px, 9vw, 132px)", lineHeight: 0.9, letterSpacing: "0.02em", color: "var(--text-0)" }}>
                LET'S<br /><span className="glow-accent" style={{ color: "var(--page-accent)" }}>TALK.</span>
              </h1>
            </Reveal>
            <Reveal delay={0.12}>
              <p className="mt-7 max-w-sm text-[15px] font-light leading-relaxed" style={{ color: "var(--text-2)" }}>
                Tell us where you're stuck. Whether it's the agency, an app, or a half-formed idea
                for the lab — we'll point you in the right direction.
              </p>
            </Reveal>
            <Reveal delay={0.18}>
              <div className="mt-10 space-y-4">
                {[[Mail, "Email", "jordan@emailink.io"], [Calendar, "Book a call", "30 min · no pitch · just work"], [MessageSquare, "Response time", "Within 1 business day"]].map(([Icon, k, v]) => (
                  <div key={k} className="flex items-center gap-4">
                    <span className="grid h-11 w-11 flex-shrink-0 place-items-center rounded-[12px]" style={{ background: tint(12), color: "var(--page-accent)" }}>
                      <Icon className="h-5 w-5" />
                    </span>
                    <div>
                      <p className="font-tech text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--text-3)" }}>{k}</p>
                      <p className="text-[14px]" style={{ color: "var(--text-1)" }}>{v}</p>
                    </div>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>

          {/* FORM */}
          <Reveal delay={0.1}>
            <SurfaceCard hover={false} className="p-7 sm:p-9" style={{ borderColor: tint(24) }}>
              {sent && (
                <div className="mb-6 flex items-center gap-3 rounded-[12px] p-4" style={{ background: tint(12), border: `1px solid ${tint(40)}` }}>
                  <span className="grid h-7 w-7 place-items-center rounded-full" style={{ background: "var(--page-accent)", color: "var(--on-accent)" }}><Check className="h-4 w-4" /></span>
                  <p className="text-[13px]" style={{ color: "var(--text-1)" }}>Thanks — your message is in. We'll be in touch shortly.</p>
                </div>
              )}
              <form onSubmit={submit} data-testid="contact-form" noValidate>
                <div className="grid gap-5">
                  <Field label="Name" error={errors.name}>
                    <input data-testid="contact-name-input" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Jane Founder" className="h-12 w-full rounded-[10px] px-4 text-[14px] outline-none" style={fieldStyle("name")} />
                  </Field>
                  <div className="grid gap-5 sm:grid-cols-2">
                    <Field label="Email" error={errors.email}>
                      <input data-testid="contact-email-input" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@brand.com" className="h-12 w-full rounded-[10px] px-4 text-[14px] outline-none" style={fieldStyle("email")} />
                    </Field>
                    <Field label="Company" optional>
                      <input data-testid="contact-company-input" value={form.company} onChange={(e) => set("company", e.target.value)} placeholder="Brandco" className="h-12 w-full rounded-[10px] px-4 text-[14px] outline-none" style={fieldStyle("company")} />
                    </Field>
                  </div>
                  <Field label="Monthly budget" optional>
                    <Select value={form.budget} onValueChange={(v) => set("budget", v)}>
                      <SelectTrigger data-testid="contact-budget-trigger" className="h-12 rounded-[10px] px-4 text-[14px]" style={fieldStyle("budget")}>
                        <SelectValue placeholder="Select a range" />
                      </SelectTrigger>
                      <SelectContent>
                        {BUDGET_OPTIONS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="What are you trying to fix?" error={errors.message}>
                    <textarea data-testid="contact-message-input" value={form.message} onChange={(e) => set("message", e.target.value)} rows={4} placeholder="A few sentences about your list, your stack and your goals…" className="w-full resize-none rounded-[10px] px-4 py-3 text-[14px] outline-none" style={fieldStyle("message")} />
                  </Field>
                  <button type="submit" data-testid="contact-form-submit-button" className="flex h-12 items-center justify-center gap-2 rounded-[12px] text-[12px] font-semibold uppercase tracking-[0.08em] transition-transform active:scale-[0.98]" style={{ background: "var(--page-accent)", color: "var(--on-accent)", boxShadow: `0 0 28px ${tint(30)}` }}>
                    Send message
                  </button>
                </div>
              </form>
            </SurfaceCard>
          </Reveal>
        </Container>
      </section>

      {/* FAQ */}
      <section className="py-20 lg:py-24">
        <Container>
          <SectionLabel index="01" total="01">FAQ</SectionLabel>
          <div className="grid gap-10 lg:grid-cols-[0.7fr_1.3fr]">
            <Reveal>
              <h2 className="text-2xl font-semibold leading-tight sm:text-3xl" style={{ color: "var(--text-0)" }}>
                Questions, answered before you ask.
              </h2>
            </Reveal>
            <Reveal delay={0.08}>
              <Accordion type="single" collapsible defaultValue="0" className="w-full">
                {CONTACT_FAQS.map((f, i) => (
                  <AccordionItem key={i} value={String(i)} className="border-b" style={{ borderColor: "var(--stroke-0)" }}>
                    <AccordionTrigger className="py-5 text-left text-[16px] font-medium hover:no-underline" style={{ color: "var(--text-0)" }}>
                      {f.q}
                    </AccordionTrigger>
                    <AccordionContent className="pb-5 text-[14px] font-light leading-relaxed" style={{ color: "var(--text-2)" }}>
                      {f.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </Reveal>
          </div>
        </Container>
      </section>
    </div>
  );
}

const Field = ({ label, error, optional, children }) => (
  <label className="block">
    <span className="font-tech mb-2 flex items-center gap-2 text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--text-2)" }}>
      {label}
      {optional && <span style={{ color: "var(--text-3)" }}>(optional)</span>}
    </span>
    {children}
    {error && <span className="mt-1.5 block text-[12px]" style={{ color: "rgba(239,120,120,0.95)" }}>{error}</span>}
  </label>
);
